/* Super Admin > Attribution Reconciliation — detect & repair broken Sales Contact
   attribution across the lifecycle. Cookie-authenticated. Mounted at /app/attribution. */
import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ShieldCheck, RefreshCw, Wrench, Download } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;
const cfg = { withCredentials: true };

const AttributionAdminPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [repairing, setRepairing] = useState(false);

  const runPreview = async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/api/admin/attribution/reconcile`, cfg); setData(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not run reconciliation"); }
    finally { setLoading(false); }
  };

  const repairAll = async () => {
    if (!window.confirm("Repair all resolvable attribution discrepancies? This creates/attaches attributed leads and stamps downstream records. All changes are audited.")) return;
    setRepairing(true);
    try {
      const r = await axios.post(`${API}/api/admin/attribution/reconcile`, {}, cfg);
      toast.success(`Repaired ${r.data.repaired} of ${r.data.total_checked} records`);
      await runPreview();
    } catch (e) { toast.error(e?.response?.data?.detail || "Repair failed"); }
    finally { setRepairing(false); }
  };

  const exportCsv = () => {
    if (!data?.discrepancies?.length) return;
    const head = ["record_type", "tenant_id", "school_name", "issue", "resolved_sales_contact", "resolvable"];
    const rows = data.discrepancies.map((d) => head.map((h) => `"${String(d[h] ?? "").replace(/"/g, '""')}"`).join(","));
    const blob = new Blob([[head.join(","), ...rows].join("\n")], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = "attribution-reconciliation.csv"; a.click();
  };

  const disc = data?.discrepancies || [];

  return (
    <div className="px-6 lg:px-10 py-8 max-w-[1400px] mx-auto" data-testid="attribution-admin-page">
      <div className="flex items-center gap-3 mb-1">
        <ShieldCheck className="h-6 w-6 text-[#007a9e]" />
        <h1 className="text-2xl font-bold text-[#111827]">Attribution Reconciliation</h1>
      </div>
      <p className="text-sm text-slate-500 mb-5">Find and repair broken or missing Sales Contact attribution. Precedence: signup → tracked token → lead → school/portfolio → quote → manual → none. The processing Super Admin is never auto-assigned.</p>

      <div className="flex flex-wrap gap-2 mb-5">
        <Button onClick={runPreview} disabled={loading} data-testid="attribution-scan-btn">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />} Scan for discrepancies
        </Button>
        {data && (
          <>
            <Button variant="outline" onClick={repairAll} disabled={repairing || data.resolvable === 0} data-testid="attribution-repair-all-btn">
              {repairing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Wrench className="h-4 w-4 mr-1" />} Repair All Resolvable ({data.resolvable})
            </Button>
            <Button variant="ghost" onClick={exportCsv} data-testid="attribution-export-btn"><Download className="h-4 w-4 mr-1" /> Export</Button>
          </>
        )}
      </div>

      {data && (
        <div className="grid grid-cols-3 gap-3 mb-5">
          <Stat label="Discrepancies" value={data.total} testid="attribution-total" />
          <Stat label="Resolvable" value={data.resolvable} testid="attribution-resolvable" cls="text-emerald-700" />
          <Stat label="Direct / Unresolved" value={data.unresolved} testid="attribution-unresolved" cls="text-slate-500" />
        </div>
      )}

      {data && (
        <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
          {disc.length === 0 ? <div className="p-10 text-center text-sm text-slate-400">No discrepancies — attribution is intact. 🎉</div> : (
            <table className="w-full text-sm min-w-[720px]">
              <thead><tr className="text-left text-[11.5px] uppercase text-slate-400 border-b border-slate-100">
                <th className="p-3">School / Account</th><th className="p-3">Issue</th><th className="p-3">Resolved Sales Contact</th><th className="p-3">Status</th>
              </tr></thead>
              <tbody>
                {disc.map((d, i) => (
                  <tr key={i} className="border-b border-slate-50" data-testid={`attribution-row-${d.tenant_id}`}>
                    <td className="p-3 font-medium text-slate-800">
                      {d.school_name || d.tenant_id}
                      <span className="ml-2 text-[11.5px] font-bold uppercase text-slate-400">
                        {d.record_type === "portfolio" ? "Portfolio" : "School"}
                      </span>
                    </td>
                    <td className="p-3 text-slate-600">{d.issue}</td>
                    <td className="p-3 text-slate-700">{d.resolved_sales_contact}</td>
                    <td className="p-3">
                      {d.repaired ? <span className="text-[11.5px] font-bold text-emerald-700 uppercase">Repaired</span>
                        : d.resolvable ? <span className="text-[11.5px] font-bold text-amber-600 uppercase">Resolvable</span>
                          : <span className="text-[11.5px] font-bold text-slate-400 uppercase">Direct Sale / No Sales Contact</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
      {!data && !loading && <div className="text-sm text-slate-400">Click "Scan for discrepancies" to begin.</div>}
    </div>
  );
};

const Stat = ({ label, value, cls = "text-slate-800", testid }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testid}>
    <div className="text-[11.5px] uppercase tracking-wide text-slate-400">{label}</div>
    <div className={`text-2xl font-bold ${cls}`}>{value}</div>
  </div>
);

export default AttributionAdminPage;
